import React, { useEffect, useState } from 'react';
import { cloneDeep, differenceWith, filter, forEach, get, isEqual, some, sortBy } from 'lodash';
import styled from 'styled-components';

import { AlertError } from '@ad-scripts/presentation/components/common/alert';
import { Body, PageHeader, Row } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import communicationQueuesResource from 'src/actions/communicationQueues/communicationQueuesResource';
import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import EntryPointDefaults from 'src/presentation/components/pages/channels/entryPoints/components/EntryPointDefaults';
import EntryPointExceptionHeader from 'src/presentation/components/pages/channels/entryPoints/components/EntryPointExceptionHeader';
import EntryPointExceptionItem from 'src/presentation/components/pages/channels/entryPoints/components/EntryPointExceptionItem';
import EntryPointsExceptions from 'src/presentation/components/pages/channels/entryPoints/components/EntryPointsEditorSection';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { FormEditorProps, useForm } from 'src/presentation/components/forms/Form';
import generateId from 'src/lib/generateId';
import InformationIcon from '@ad-scripts/presentation/components/common/icons/fill/information-fill';
import SectionHeaderTitleAndDescription from 'src/presentation/components/pages/channels/entryPoints/components/SectionHeaderTitleAndDescription';
import { STATUS_PERSISTING, STATUS_SUCCEEDED, useResourceValues } from 'src/presentation/hooks/useResource';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import useDirtyFormConfirmation from 'src/presentation/components/pages/shared/hooks/useDirtyFormConfirmation';
import useEntryPointsChangeDetection from 'src/presentation/components/pages/channels/entryPoints/hooks/useEntryPointsChangeDetection';
import useEntryPointType from 'src/presentation/components/pages/channels/entryPoints/hooks/useEntryPointType';
import useInboxes from 'src/presentation/components/pages/channels/entryPoints/hooks/useInboxes';

const EntryPointsEditor = ({ item, errors }: FormEditorProps) => {
  const itemType = item.id;
  const dataAid = `entry-point-section-${itemType}`;

  const { inboxesMap, inboxesOptions } = useInboxes();
  const { config: entryPointInfo } = useEntryPointType(item.id);

  const { persistingState } = useResourceValues(communicationQueuesResource);

  const [isComplete, setComplete] = useState<boolean>();

  const formContext = useForm({
    errors: errors ? errors[itemType] : [],
    item: entryPointInfo,
    manualScrollToFirstError: true,
    resetErrors: attr => communicationQueuesResource.actions.resetEntryPointsErrors({ sectionType: itemType, attr }),
    root: 'entry-points',
  });

  const { workingState, updateState, onClose } = formContext;

  const { saveChanges, deleteException } = useEntryPointsChangeDetection(formContext);
  const { handleCancel, handleStay, isConfirmCancelModalVisible, isModified } = useDirtyFormConfirmation({
    formContext,
    isComplete,
    setComplete,
  });

  useEffect(() => {
    if (persistingState === STATUS_SUCCEEDED) {
      setComplete(true);
    }
  }, [persistingState, setComplete]);

  const defaultsIds = workingState?.defaultsIds ?? [];

  const entryPoints = entryPointInfo.communicationQueues;

  const getNewRowWithDefaultData = () => {
    return {
      communicationQueues: [],
      id: generateId(),
      routingGroupId: '',
      slaSeconds: '',
    };
  };

  const hasDeactivatedInboxes = some(
    formContext.errors,
    (error: any): boolean => error.code === 'invalid_state' && error.attr.endsWith('routingGroupId')
  );

  const content = (
    <div>
      {hasDeactivatedInboxes && (
        <StyledAlertError>
          <b>Note:</b> At least one of your Inboxes has been deactivated. Select another Inbox.
        </StyledAlertError>
      )}
      <EntryPointDefaults
        className="entryPointsDefaults"
        data-aid={`${dataAid}-${itemType}`}
        inboxes={inboxesOptions}
        inboxesMap={inboxesMap}
      />
    </div>
  );

  const exceptionsTitle = (
    <ExceptionsTitle>
      Exceptions
      <Tooltip data-aid="column-tooltip" message="Entry Points not using the Default Inbox">
        <StyledInformationIcon />
      </Tooltip>
    </ExceptionsTitle>
  );

  const getEntryPointsGroups = (index: number, value: string[]) => {
    const draft = cloneDeep(workingState);
    const exceptionsGrouped = get(draft, 'exceptionsGrouped', []);

    forEach(exceptionsGrouped, (group, n: number) => {
      if (n === index) {
        const previous = group.communicationQueues;

        const addedCommQueues = differenceWith(value, previous, isEqual);
        if (addedCommQueues.length) {
          draft.defaultsIds = sortBy(filter(draft.defaultsIds, commQueueId => !addedCommQueues.includes(commQueueId)));
        }

        const removedCommQueues = differenceWith(previous, value, isEqual);
        if (removedCommQueues.length) {
          draft.defaultsIds.push(...removedCommQueues);
        }

        group.communicationQueues = sortBy(value);
      } else {
        const communicationQueues = get(group, 'communicationQueues', []);
        group.communicationQueues = filter(communicationQueues, q => !value.includes(q));
      }
    });

    return draft;
  };

  const handleSelect = (index: number, value: string[]): void => {
    const entryPointsGroups: any = getEntryPointsGroups(index, value);

    const newState = cloneDeep(entryPointsGroups);

    updateState(
      { exceptionsGrouped: newState.exceptionsGrouped, defaultsIds: newState.defaultsIds },
      { replaceArrayValues: true }
    );
  };

  const renderItem = ({ index }) => (
    <EntryPointExceptionItem
      index={index}
      inboxes={inboxesOptions}
      inboxesMap={inboxesMap}
      endpoints={entryPointInfo.endpointsOptions as any}
      onSelect={handleSelect}
    />
  );

  const showExceptions = !!entryPoints.length;

  return (
    <>
      <Form context={formContext} preventSaveOnEnter>
        <PageHeader headerText={`Edit Entry Points for ${item.name}`} descriptionText="" />
        <StyledBody>
          <Row>
            <SectionHeaderTitleAndDescription
              data-aid={`${dataAid}-header`}
              entryPoints={entryPoints}
              inboxesMap={inboxesMap}
              item={item}
            />
          </Row>

          <Row>{content}</Row>
          {showExceptions && (
            <>
              {exceptionsTitle}
              <EntryPointsExceptions
                addButtonLabel="Add exception"
                className="entryPointTypeData"
                hideAddButton={!defaultsIds.length}
                attr="exceptionsGrouped"
                errors={errors ? errors[itemType] : []}
                data-aid={`${dataAid}-container`}
                getNewItemDefaultData={getNewRowWithDefaultData}
                renderItem={renderItem}
                prefixWord={null}
                onDelete={deleteException}
                tableHeader={<EntryPointExceptionHeader />}
                hideOptions={!entryPointInfo.endpointsOptions.length}
                getItemKey={item => `${item.id}+++${item.endpointId}+++${item.routingSelector}`}
              />
            </>
          )}
        </StyledBody>

        <Footer>
          <Button data-aid="cancel-button" type={Button.Types.TEXT} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            data-aid="save-button"
            disabled={!isModified}
            isLoading={persistingState === STATUS_PERSISTING}
            onClick={saveChanges}
          >
            Save
          </Button>
        </Footer>
      </Form>
      <ConfirmCancelModal
        name="Entry Point"
        handleLeave={onClose}
        handleStay={handleStay}
        isModified={isModified && !isComplete}
        isVisible={isConfirmCancelModalVisible}
        customText="If you leave this page, your changes will be discarded."
      />
    </>
  );
};

export default EntryPointsEditor;

const StyledBody = styled(Body)`
  max-width: 780px;
`;

const StyledAlertError = styled(AlertError)`
  margin: 0 0 24px 0;
  max-width: 752px;
  width: inherit;
`;

const ExceptionsTitle = styled.h3`
  display: flex;
  gap: ${p => p.theme.spacing.stackXSmall};
`;

const StyledInformationIcon = styled(InformationIcon)`
  fill: ${p => p.theme.colors.gray600};
  height: 15px;
  position: relative;
  top: 1px;
  width: 15px;
`;
